/**
 * PlayerSegmentLibrary
 *
 * Browse, preview, edit, and delete a player's saved notable segments.
 * Used as Tab 2 in the multi-source clip picker inside LessonVideoEditor.
 */

import { useState, useEffect, useRef } from 'react';
import type { OfferingType } from '../types/lesson';
import { SkeletonGroup, ListRowSkeleton } from './Skeleton';
import type { OriginalVideo, SavedPlayerSegment } from '../types/videoEdit';
import {
  getPlayerSavedSegments,
  updateSavedSegment,
  deleteSavedSegment,
} from '../services/playerLibrary';
import { getOriginalVideo, getOriginalVideoUrl } from '../services/videoEditDraft';
import { getLesson } from '../services/lessons';
import { getOrder } from '../services/orders';
import { BrowseRow } from './videoEditor/pickerRows';
import { formatOfferingLabel } from '../utils/offeringLabel';
import { rangeLabel, rowName } from './videoEditor/rowText';
import { RowThumbnail } from './videoEditor/rowThumbnail';

interface LessonMeta {
  offeringType: OfferingType;
  lessonNumber?: number;
  totalLessons?: number;
}

interface PlayerSegmentLibraryProps {
  playerId: string;
  /** Filter out segments from this video (already shown in Current Video tab) */
  currentOriginalVideoId?: string;
  /**
   * Scope, not a filter. `players/{id}/savedSegments` holds everything every
   * coach ever saved for this player; only this coach's belong in this coach's
   * picker. `savedBy` is written on every upsert, so the match is exact rather
   * than approximate.
   */
  coachId: string;
  /** Called when user selects a segment for use as clip source */
  onSelect?: (seg: SavedPlayerSegment) => void;
  /** Id of the currently selected segment, so the row can show it. */
  selectedSegmentId?: string;
  /** Called after a segment's label/note is saved, so callers can mirror the update */
  onSegmentUpdated?: (seg: SavedPlayerSegment) => void;
  /** When true, hide edit/delete controls and use the dark-theme "Use" button style */
  compact?: boolean;
}

function formatTime(s: number): string {
  const m = Math.floor(s / 60);
  return `${m}:${Math.floor(s % 60).toString().padStart(2, '0')}`;
}

function formatDate(iso: string): string {
  return new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}


export function PlayerSegmentLibrary({ playerId, currentOriginalVideoId, coachId, onSelect, selectedSegmentId, onSegmentUpdated, compact }: PlayerSegmentLibraryProps) {
  const [segments, setSegments] = useState<SavedPlayerSegment[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
    // originalVideoId → OriginalVideo metadata (null if fetch failed)
  const [videoMeta, setVideoMeta] = useState<Record<string, OriginalVideo | null>>({});
  // lessonId → lesson offering metadata (null if fetch failed)
  const [lessonMeta, setLessonMeta] = useState<Record<string, LessonMeta | null>>({});

  // Inline edit state: segId → { label, note }
  const [editing, setEditing] = useState<Record<string, { label: string; note: string }>>({});
  // Preview state: segId → video url (loading | loaded)
  const [previewing, setPreviewing] = useState<Record<string, string | 'loading'>>({});
  // Delete confirm
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);
  // Saving per segment
  const [saving, setSaving] = useState<Record<string, boolean>>({});

  const videoRefs = useRef<Record<string, HTMLVideoElement | null>>({});

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    getPlayerSavedSegments(playerId)
      .then((segs) => {
        if (cancelled) return;
        setSegments(segs);
        setLoading(false);

        // Fetch video metadata for each unique originalVideoId
        const unique = Array.from(new Map(segs.map((s) => [s.originalVideoId, s])).values());
        unique.forEach(async (rep) => {
          try {
            const vid = await getOriginalVideo(rep.context, rep.originalVideoId);
            if (!cancelled) setVideoMeta((m) => ({ ...m, [rep.originalVideoId]: vid ?? null }));
          } catch {
            if (!cancelled) setVideoMeta((m) => ({ ...m, [rep.originalVideoId]: null }));
          }
        });

        // Fetch lesson+order metadata for each unique lessonId
        const uniqueLessons = Array.from(
          new Map(segs.filter((s) => s.lessonId).map((s) => [s.lessonId!, s])).values()
        );
        uniqueLessons.forEach(async (rep) => {
          const lessonId = rep.lessonId!;
          try {
            const lesson = await getLesson(lessonId);
            if (!lesson) { if (!cancelled) setLessonMeta((m) => ({ ...m, [lessonId]: null })); return; }
            const order = await getOrder(lesson.orderId);
            if (!cancelled) setLessonMeta((m) => ({
              ...m,
              [lessonId]: order ? {
                offeringType: order.type,
                lessonNumber: lesson.lessonNumber,
                totalLessons: order.totalLessons,
              } : null,
            }));
          } catch {
            if (!cancelled) setLessonMeta((m) => ({ ...m, [lessonId]: null }));
          }
        });
      })
      .catch(() => { if (!cancelled) { setError('Failed to load saved segments.'); setLoading(false); } });
    return () => { cancelled = true; };
  }, [playerId]);

  // Seek preview video to startTime on load
  function handleVideoLoaded(seg: SavedPlayerSegment, videoEl: HTMLVideoElement) {
    videoEl.currentTime = seg.startTime;
  }

  async function handleTogglePreview(seg: SavedPlayerSegment) {
    if (previewing[seg.id]) {
      // Close preview
      setPreviewing((p) => { const n = { ...p }; delete n[seg.id]; return n; });
      return;
    }
    setPreviewing((p) => ({ ...p, [seg.id]: 'loading' }));
    try {
      const originalVideo = await getOriginalVideo(seg.context, seg.originalVideoId);
      if (!originalVideo) throw new Error('Video not found');
      const url = await getOriginalVideoUrl(originalVideo);
      setPreviewing((p) => ({ ...p, [seg.id]: url }));
    } catch {
      setPreviewing((p) => { const n = { ...p }; delete n[seg.id]; return n; });
    }
  }

  function startEditing(seg: SavedPlayerSegment) {
    setEditing((e) => ({ ...e, [seg.id]: { label: seg.label, note: seg.note ?? '' } }));
  }

  async function commitEdit(seg: SavedPlayerSegment) {
    const ed = editing[seg.id];
    if (!ed || !ed.label.trim()) return;
    setSaving((s) => ({ ...s, [seg.id]: true }));
    try {
      const label = ed.label.trim();
      const note = ed.note.trim() || undefined;
      await updateSavedSegment(playerId, seg.id, { label, note });
      const updated = { ...seg, label, note };
      setSegments((prev) => prev.map((s) => s.id === seg.id ? updated : s));
      setEditing((e) => { const n = { ...e }; delete n[seg.id]; return n; });
      onSegmentUpdated?.(updated);
    } finally {
      setSaving((s) => { const n = { ...s }; delete n[seg.id]; return n; });
    }
  }

  function cancelEdit(segId: string) {
    setEditing((e) => { const n = { ...e }; delete n[segId]; return n; });
  }

  async function handleDelete(segId: string) {
    try {
      await deleteSavedSegment(playerId, segId);
      setSegments((prev) => prev.filter((s) => s.id !== segId));
    } finally {
      setConfirmDelete(null);
    }
  }

  if (loading) {
    return (
      <SkeletonGroup label="Loading clips">
        {Array.from({ length: 4 }, (_, i) => (
          <ListRowSkeleton key={i} height={56} avatar={48} avatarRadius={6} padding="8px 0" />
        ))}
      </SkeletonGroup>
    );
  }

  if (error) {
    return <p className="text-red-500 text-sm px-1 py-4">{error}</p>;
  }

  const filtered = segments
    .filter((s) => !currentOriginalVideoId || s.originalVideoId !== currentOriginalVideoId)
    .filter((s) => s.savedBy === coachId);

  if (filtered.length === 0) {
    return (
      <p className="text-gray-400 text-sm px-1 py-4">
        {segments.length > 0
          ? 'Nothing from your other lessons with this player yet.'
          : 'Nothing saved yet. Segments are kept here as you edit a lesson.'}
      </p>
    );
  }

  filtered.sort((a, b) => {
    const da = a?.startTime;
    const db = b?.startTime;
    if (da != null && db != null && da!=db) {
      return (da > db ? 1 : -1);
    }
    return 0;
  });

  // Group by originalVideoId, preserving order of first occurrence
  const groupOrder: string[] = [];
  const groups: Record<string, SavedPlayerSegment[]> = {};
  for (const seg of filtered) {
    if (!groups[seg.originalVideoId]) {
      groupOrder.push(seg.originalVideoId);
      groups[seg.originalVideoId] = [];
    }
    groups[seg.originalVideoId].push(seg);
  }

  // Sort groups: videos with known createdAt newest first; unknown last
  groupOrder.sort((a, b) => {
    const da = videoMeta[a]?.createdAt;
    const db = videoMeta[b]?.createdAt;
    if (da && db) return db.localeCompare(da);
    if (da) return -1;
    if (db) return 1;
    return 0;
  });

  return (
    <div className="space-y-4">
      {groupOrder.map((videoId) => {
        const segs = groups[videoId];
        const meta = videoMeta[videoId];
        /* Two fields, same order, every group: what the lesson was, and when.
           The old header led with the video title, which is optional — groups
           without one fell through to a bare date and read as a different kind
           of thing. Title is dropped rather than made conditional. */
        const lessonId = segs[0]?.lessonId;
        const lMeta = lessonId ? lessonMeta[lessonId] : undefined;
        /* "Lesson" where the offering is unknown, and it is unknown for a real
           reason rather than a rendering one: `lessonId` is optional on
           SavedPlayerSegment, so any segment saved without one has nothing to
           look up, and getLesson/getOrder can also fail. Asserting "Single
           Lesson" there would be inventing the answer — formatOfferingLabel
           returns that string for a *known* offering. Same shape, same
           treatment; the word is less specific because the data is. */
        const offeringLabel = lMeta ? formatOfferingLabel(lMeta) : 'Lesson';
        const groupDate = formatDate(meta?.createdAt ?? segs[0].savedAt);

        return (
          <div key={videoId}>
            {/* Group header */}
            <div className="flex items-center gap-1.5 mb-1.5 px-0.5">
              <p className={`text-[10px] font-semibold uppercase tracking-wider truncate ${compact ? 'text-[var(--editor-text-muted)]' : 'text-gray-700'}`}>
                {offeringLabel} · {groupDate}
              </p>
            </div>

            <div className="space-y-1.5">
              {segs.map((seg, segIdx) => {
        const ed = editing[seg.id];
        const previewUrl = previewing[seg.id];
        const isEditing = !!ed;
        const isSaving = saving[seg.id];

        /* The compact path is the picker's; it gets the picker's row. The
           branch below is the standalone library's own editing UI and is
           untouched. */
        if (compact && onSelect && !isEditing) {
          /* One rule, the same as every other list: the name leads, the range
             follows. It used to lead with the range whenever the name was a
             default, and to repeat the save date that the group header already
             carries. Available per segment: label, note, startTime, endTime,
             savedAt, savedByName, lessonId, originalVideoId. */
          const range = rangeLabel(seg.startTime, seg.endTime);
          const note = seg.note?.trim();
          return (
            <div key={seg.id} data-testid="player-library-segment">
              <BrowseRow
                thumb={(
                  <RowThumbnail
                    cacheKey={`lib:${seg.id}`}
                    time={seg.startTime}
                    /* The URL is not on the segment — it is rebuilt from
                       context + originalVideoId. Same two calls the preview
                       already makes, awaited rather than reworked. */
                    resolveVideoUrl={async () => {
                      const ov = await getOriginalVideo(seg.context, seg.originalVideoId);
                      return ov ? await getOriginalVideoUrl(ov) : null;
                    }}
                  />
                )}
                primary={rowName(seg.label, segIdx)}
                secondary={note ? `${note} · ${range}` : range}
                selected={selectedSegmentId === seg.id}
                onSelect={() => onSelect(seg)}
                previewing={!!previewUrl && previewUrl !== 'loading'}
                onTogglePreview={() => handleTogglePreview(seg)}
              >
                {previewUrl && previewUrl !== 'loading' && (
                  <div className="border-t border-[var(--editor-border)] bg-black">
                    <video
                      ref={(el) => { videoRefs.current[seg.id] = el; }}
                      src={previewUrl}
                      controls
                      className="w-full max-h-40 object-contain"
                      onLoadedMetadata={(e) => { e.currentTarget.currentTime = seg.startTime; }}
                    />
                  </div>
                )}
              </BrowseRow>
            </div>
          );
        }

        return (
          <div
            key={seg.id}
            data-testid="player-library-segment"
            className="rounded-lg overflow-hidden"
            style={compact
              ? { border: '1px solid var(--editor-border)', background: 'var(--editor-surface)' }
              : { border: '1px solid #e5e7eb' }}
          >
            {/* Main row */}
            <div className="flex items-start gap-2 px-3 py-2">
              <div className="flex-1 min-w-0">
                {isEditing ? (
                  <div className="space-y-1.5">
                    <input
                      type="text"
                      value={ed.label}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [seg.id]: { ...ed, label: e.target.value } }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(seg); if (e.key === 'Escape') cancelEdit(seg.id); }}
                      className="w-full px-2 py-1 text-[16px] leading-[21px] border border-orange-400 rounded focus:outline-none focus:ring-2 focus:ring-orange-300 input-glow"
                      autoFocus
                      disabled={isSaving}
                    />
                    <input
                      type="text"
                      value={ed.note}
                      onChange={(e) => setEditing((prev) => ({ ...prev, [seg.id]: { ...ed, note: e.target.value } }))}
                      onKeyDown={(e) => { if (e.key === 'Enter') commitEdit(seg); if (e.key === 'Escape') cancelEdit(seg.id); }}
                      placeholder="Note (optional)"
                      className="w-full px-2 py-1 text-[16px] leading-[21px] border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-gray-400 input-glow"
                      disabled={isSaving}
                    />
                    <div className="flex gap-1.5">
                      <button onClick={() => commitEdit(seg)} disabled={!ed.label.trim() || isSaving} className="px-2 py-0.5 text-xs bg-orange-500 text-white rounded hover:bg-orange-600 disabled:opacity-50">
                        Save
                      </button>
                      <button onClick={() => cancelEdit(seg.id)} disabled={isSaving} className="px-2 py-0.5 text-xs border border-gray-300 rounded hover:bg-gray-50">
                        Cancel
                      </button>
                    </div>
                  </div>
                ) : (
                  <>
                    <p className={`text-sm font-medium truncate ${compact ? 'text-[var(--editor-text)]' : 'text-gray-900'}`}>{seg.label}</p>
                    {seg.note && <p className={`text-xs truncate ${compact ? 'text-[var(--editor-text-muted)]' : 'text-gray-500'}`}>{seg.note}</p>}
                    <p className={`text-xs mt-0.5 ${compact ? 'text-[var(--editor-text-muted)]' : 'text-gray-400'}`}>
                      {formatTime(seg.startTime)} – {formatTime(seg.endTime)}
                    </p>
                  </>
                )}
              </div>

              {/* Action buttons */}
              {!isEditing && (
                <div className="flex items-center gap-1 flex-shrink-0">
                  {/* Preview */}
                  <button
                    onClick={() => handleTogglePreview(seg)}
                    className={`w-7 h-7 flex items-center justify-center rounded transition-colors ${
                      compact
                        ? `text-[var(--editor-text-muted)] hover:text-[var(--editor-text)] hover:bg-[var(--editor-hover)] ${previewUrl && previewUrl !== 'loading' ? 'text-[var(--editor-text)] bg-[var(--editor-hover)]' : ''}`
                        : `text-gray-500 hover:text-blue-600 hover:bg-blue-50 ${previewUrl && previewUrl !== 'loading' ? 'text-blue-600 bg-blue-50' : ''}`
                    }`}
                    title={previewUrl ? 'Close preview' : 'Preview clip'}
                  >
                    {previewUrl === 'loading' ? (
                      <div className={`w-3.5 h-3.5 border rounded-full animate-spin ${compact ? 'border-[var(--editor-text-muted)] border-t-[var(--editor-text)]' : 'border-gray-400 border-t-gray-600'}`} />
                    ) : previewUrl ? (
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M6 18L18 6M6 6l12 12" stroke="currentColor" strokeWidth="2" strokeLinecap="round" fill="none"/></svg>
                    ) : (
                      <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 24 24"><path d="M8 5v14l11-7z"/></svg>
                    )}
                  </button>

                  {/* Edit — hidden in compact mode */}
                  {!compact && (
                    <button
                      onClick={() => startEditing(seg)}
                      className="w-7 h-7 flex items-center justify-center rounded text-gray-500 hover:text-gray-800 hover:bg-gray-100 transition-colors"
                      title="Edit label / note"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                  )}

                  {/* Delete — hidden in compact mode */}
                  {!compact && (
                    <button
                      onClick={() => setConfirmDelete(seg.id)}
                      className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-red-50 transition-colors"
                      title="Delete"
                    >
                      <svg className="w-3.5 h-3.5" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  )}

                  {/* Select (only when onSelect is provided) */}
                  {onSelect && (
                    <button
                      onClick={() => onSelect(seg)}
                      className={`ml-1 px-2 py-1 text-xs rounded transition-colors ${
                        compact
                          ? 'bg-[var(--color-biscayne)] text-white hover:bg-[var(--color-db-accent-hover)]'
                          : 'bg-orange-500 text-white hover:bg-orange-600'
                      }`}
                    >
                      Use
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* Inline video preview */}
            {previewUrl && previewUrl !== 'loading' && (
              <div className={`border-t bg-black ${compact ? 'border-[var(--editor-border)]' : 'border-gray-100'}`}>
                <video
                  ref={(el) => { videoRefs.current[seg.id] = el; }}
                  src={previewUrl}
                  controls
                  className="w-full max-h-40 object-contain"
                  onLoadedMetadata={(e) => handleVideoLoaded(seg, e.currentTarget)}
                />
              </div>
            )}

            {/* Delete confirmation inline — hidden in compact mode */}
            {!compact && confirmDelete === seg.id && (
              <div className="border-t border-red-100 bg-red-50 px-3 py-2 flex items-center justify-between gap-2">
                <p className="text-xs text-red-700">Delete "{seg.label}"?</p>
                <div className="flex gap-1.5">
                  <button onClick={() => setConfirmDelete(null)} className="px-2 py-0.5 text-xs border border-gray-300 bg-white rounded hover:bg-gray-50">Cancel</button>
                  <button onClick={() => handleDelete(seg.id)} className="px-2 py-0.5 text-xs bg-red-600 text-white rounded hover:bg-red-700">Delete</button>
                </div>
              </div>
            )}
                  </div>
                );
              })}
            </div>            
          </div>
        );
      })}
    </div>
  );
}
